import React, { useEffect, useRef, useState } from 'react';
import { appsAPI } from '../api/appsAPI';
import { useAuth } from '../../auth/hooks/useAuth';
import { cleanSubjects, OXBRIDGE_PACKAGE } from '../../marketing/pricing/selection';
import './ChooseExamDialog.css';

// The five sold exams. `exam` is the value POST /api/purchases/beginnings takes.
export const EXAM_CHOICES = [
  { exam: 'UCAT', label: 'UCAT', sub: 'Medicine and dentistry admissions' },
  { exam: 'TMUA', label: 'TMUA', sub: 'Maths admissions at Cambridge, Warwick and others' },
  { exam: 'ESAT', label: 'ESAT', sub: 'Engineering and sciences at Cambridge and Imperial' },
  { exam: 'LNAT', label: 'LNAT', sub: 'Law admissions' },
  { exam: 'TARA', label: 'TARA', sub: 'Thinking and reasoning at Oxford' },
];

export const PACKAGE_CHOICES = [
  { key: 'medicine', label: 'Medicine', sub: 'Medical school interview practice' },
  { key: 'dentistry', label: 'Dentistry', sub: 'Dental school interview practice' },
  { key: OXBRIDGE_PACKAGE, label: 'Oxbridge', sub: 'Oxford and Cambridge interview practice, for one subject' },
  { key: 'degree_apprenticeship', label: 'Degree Apprenticeship', sub: 'Interview practice for degree apprenticeships' },
];

const SAVE_ERROR = 'That did not save. Please try again.';

/**
 * Whether the account must be asked to choose an exam: the server has said
 * what it owns (`access.owned` is an object), it owns no exam, it is not
 * `all_tracks`, and it may purchase (not an organisation member). An unreadable
 * account (`owned` null or absent) never shows it.
 */
export function needsExamChoice(user) {
  const access = user && user.access;
  if (!access || access.can_purchase !== true) return false;
  const owned = access.owned;
  if (!owned || typeof owned !== 'object' || Array.isArray(owned)) return false;
  if (owned.all_tracks === true || access.all_tracks === true) return false;
  return !Array.isArray(owned.exams) || owned.exams.length === 0;
}

function holdsPackage(user) {
  const packages = user?.access?.owned?.interviews?.packages;
  return Array.isArray(packages) && packages.length > 0;
}

/**
 * One-time exam choice (plans-and-packages F5, Q23). Shown to a signed-in
 * account that holds no exam; it cannot be dismissed without choosing. Step
 * one picks the exam, step two optionally a package ("Not now" skips the
 * package only). The choice is saved by POST /api/purchases/beginnings and the
 * account's `access.owned` is replaced with the server's answer. It is
 * one-time by state: once an exam is held `needsExamChoice` is false. Nothing
 * is granted client-side, and a failed save keeps the dialog open.
 */
const ChooseExamDialog = () => {
  const { user, updateUser } = useAuth();
  const [step, setStep] = useState('exam');
  const [exam, setExam] = useState('');
  const [pkg, setPkg] = useState('');
  const [subject, setSubject] = useState('');
  const [subjects, setSubjects] = useState({ status: 'loading', list: [] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const headingRef = useRef(null);
  const subjectsRequested = useRef(false);
  const lastWithPackage = useRef(false);

  const open = needsExamChoice(user);
  const showPackages = !holdsPackage(user);

  useEffect(() => {
    if (open && headingRef.current) headingRef.current.focus();
  }, [open, step]);

  // The public subject list is fetched once, the first time Oxbridge is picked.
  useEffect(() => {
    if (!open || pkg !== OXBRIDGE_PACKAGE || subjectsRequested.current) return undefined;
    subjectsRequested.current = true;
    let live = true;
    setSubjects({ status: 'loading', list: [] });
    Promise.resolve()
      .then(() => appsAPI.getOxbridgeSubjects())
      .then((body) => {
        if (live) setSubjects({ status: 'ready', list: cleanSubjects(body) });
      })
      .catch(() => {
        if (live) setSubjects({ status: 'failed', list: [] });
      });
    return () => { live = false; };
  }, [open, pkg]);

  if (!open) return null;

  const oxbridgeNeedsSubject = pkg === OXBRIDGE_PACKAGE && !subject;
  const canSubmitPackage = Boolean(pkg) && !oxbridgeNeedsSubject;

  const submit = async (withPackage) => {
    if (saving || !exam) return;
    lastWithPackage.current = withPackage;
    const body = { exam };
    if (withPackage && pkg) {
      body.package = pkg;
      if (pkg === OXBRIDGE_PACKAGE) body.subject = subject;
    }
    setSaving(true);
    setError('');
    try {
      const res = await appsAPI.chooseBeginnings(body);
      const owned = res && res.owned && typeof res.owned === 'object' ? res.owned : await appsAPI.getOwned();
      const access = { ...(user.access || {}), owned };
      if (typeof owned.has_paid === 'boolean') access.has_paid = owned.has_paid;
      updateUser({ access });
    } catch (err) {
      setError(SAVE_ERROR);
    } finally {
      setSaving(false);
    }
  };

  const pickExam = (value) => {
    setExam(value);
    setError('');
    setStep(showPackages ? 'package' : 'confirm');
  };

  const examLabel = (EXAM_CHOICES.find((c) => c.exam === exam) || {}).label || exam;

  return (
    <div className="choose-exam-backdrop">
      <div className="choose-exam" role="dialog" aria-modal="true" aria-labelledby="choose-exam-title">
        {step === 'exam' ? (
          <>
            <h2 id="choose-exam-title" className="choose-exam__title" tabIndex={-1} ref={headingRef}>
              Which exam are you preparing for?
            </h2>
            <p className="choose-exam__body">
              Pick one to start free. Beginnings gives you a first set of practice papers for that exam.
            </p>
            <ul className="choose-exam__list">
              {EXAM_CHOICES.map((choice) => (
                <li key={choice.exam}>
                  <button type="button" className="choose-exam__row" onClick={() => pickExam(choice.exam)}>
                    <span className="choose-exam__row-label">{choice.label}</span>
                    <span className="choose-exam__row-sub">{choice.sub}</span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <>
            <h2 id="choose-exam-title" className="choose-exam__title" tabIndex={-1} ref={headingRef}>
              {showPackages ? 'Add interview practice?' : `Start ${examLabel} free`}
            </h2>
            <p className="choose-exam__body">
              {showPackages
                ? `You are starting ${examLabel} free. You can also start one interview package free, or skip this for now.`
                : `Beginnings gives you a first set of ${examLabel} practice papers.`}
            </p>
            {showPackages ? (
              <ul className="choose-exam__list">
                {PACKAGE_CHOICES.map((choice) => (
                  <li key={choice.key}>
                    <button
                      type="button"
                      className={`choose-exam__row${pkg === choice.key ? ' choose-exam__row--on' : ''}`}
                      aria-pressed={pkg === choice.key}
                      onClick={() => { setPkg(choice.key); setSubject(''); setError(''); }}
                    >
                      <span className="choose-exam__row-label">{choice.label}</span>
                      <span className="choose-exam__row-sub">{choice.sub}</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            {showPackages && pkg === OXBRIDGE_PACKAGE ? (
              <div className="choose-exam__subject">
                <label htmlFor="choose-exam-subject">Oxbridge subject</label>
                <select
                  id="choose-exam-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  disabled={subjects.status !== 'ready'}
                >
                  <option value="">
                    {subjects.status === 'failed' ? 'Subjects unavailable' : 'Choose a subject'}
                  </option>
                  {subjects.list.map((name) => <option key={name} value={name}>{name}</option>)}
                </select>
              </div>
            ) : null}
            {error ? (
              <p className="choose-exam__error" role="alert">
                {error}
                {' '}
                <button type="button" className="choose-exam__quiet" onClick={() => submit(lastWithPackage.current)} disabled={saving}>
                  Try again
                </button>
              </p>
            ) : null}
            <div className="choose-exam__actions">
              <button
                type="button"
                className="choose-exam__btn"
                onClick={() => submit(showPackages)}
                disabled={saving || (showPackages && !canSubmitPackage)}
              >
                {showPackages ? 'Start free' : 'Continue'}
              </button>
              {showPackages ? (
                <button type="button" className="choose-exam__quiet" onClick={() => submit(false)} disabled={saving}>
                  Not now
                </button>
              ) : null}
              <button type="button" className="choose-exam__quiet" onClick={() => { setStep('exam'); setError(''); }} disabled={saving}>
                Back
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChooseExamDialog;
